import {
  GridCallbackDetails,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash.debounce';
import isEqual from 'lodash.isequal';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { FilterParam } from 'common/src/query-params/FilterParam';
import { SingleOperators } from 'common/src/query-params/operators';
import { DataFileType } from 'common/src/types/enums/DataFileType.enum';
import { IImportFile, IPage, IStatement } from 'common/src/types/interfaces';

import { QUERY_KEYS } from 'portal/src/api/constants';
import { getImportFiles } from 'portal/src/api/ImportFileService';
import { getStatements } from 'portal/src/api/StatementsService';
import DataTable from 'portal/src/components/common/DataTable';
import { useTableStateContext } from 'portal/src/providers/TableProviderContext';

import {
  getGridColumns,
  UploadHistoryTableTagConfigs,
} from './UploadHistoryTable.columns';
import {
  getValidFilterItems,
  uploadDefaultSort,
} from './UploadHistoryTable.filtering';

type UploadHistoryRow = IStatement | IImportFile;

type UploadHistoryTableBaseProps = {
  contextName: string;
  isSysAdmin: boolean;
};

type BillUploadHistoryTableProps = UploadHistoryTableBaseProps & {
  variant: 'bill';
  setShowPicPreview: (statementId: string | null) => void;
};

type SpreadsheetUploadHistoryTableProps = UploadHistoryTableBaseProps & {
  variant: 'spreadsheet';
  loading: boolean;
  tagConfigs: UploadHistoryTableTagConfigs;
};

type UploadHistoryTableProps =
  | BillUploadHistoryTableProps
  | SpreadsheetUploadHistoryTableProps;

const getInitialColumnVisibilityModel = (
  variant: UploadHistoryTableProps['variant'],
): GridColumnVisibilityModel =>
  variant === 'bill'
    ? {
        statusTimestamp: false,
        createdBy: false,
      }
    : {
        statusTimestamp: false,
      };

const UploadHistoryTable = ({
  contextName,
  isSysAdmin,
  ...props
}: UploadHistoryTableProps) => {
  const navigate = useNavigate();
  const isBillUpload = props.variant === 'bill';
  const isSpreadsheetUpload = props.variant === 'spreadsheet';
  const { filter: defaultFilter = { items: [] }, sort: defaultSort } =
    useTableStateContext()[contextName];

  const [filterModel, setFilterModel] =
    useState<GridFilterModel>(defaultFilter);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 100,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>(
    defaultSort ?? uploadDefaultSort,
  );
  const [serverFilterModel, setServerFilterModel] = useState<GridFilterModel>({
    ...defaultFilter,
    items: getValidFilterItems(defaultFilter),
  });
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(() =>
      getInitialColumnVisibilityModel(props.variant),
    );

  const {
    data: uploadHistory,
    isFetching,
    isError,
  } = useQuery({
    queryKey: [
      isBillUpload ? QUERY_KEYS.statements : QUERY_KEYS.importFiles,
      serverFilterModel,
      paginationModel,
      sortModel,
    ],
    queryFn: async ({ queryKey }) => {
      const [_, filter, page, sort] = queryKey as [
        string,
        GridFilterModel,
        GridPaginationModel,
        GridSortModel,
      ];
      const automatedFileTypeRestriction = {
        field: 'type',
        value: DataFileType.Automated,
        operator: SingleOperators.NOT_EQUAL_TO,
      };
      const filters = [...filter.items] as FilterParam[];

      if (!isSysAdmin) {
        filters.push(automatedFileTypeRestriction);
      }

      if (isBillUpload) {
        return (await getStatements({
          limit: page.pageSize,
          page: page.page,
          sort: sort[0]?.field,
          sortDirection: sort[0]?.sort,
          filters,
        })) as IPage<UploadHistoryRow>;
      }

      return (await getImportFiles({
        limit: page.pageSize,
        page: page.page,
        sort: sort[0]?.field,
        sortDirection: sort[0]?.sort,
        filters: filter.items as FilterParam[],
      })) as IPage<UploadHistoryRow>;
    },
    initialData: { data: [] } as IPage<UploadHistoryRow>,
  });

  const updateServerFilterModel = useCallback(
    (nextFilter: GridFilterModel) => {
      const nextItems = getValidFilterItems(nextFilter);

      if (!isEqual(nextItems, serverFilterModel.items)) {
        setServerFilterModel({
          ...nextFilter,
          items: nextItems,
        });

        setPaginationModel((old) => ({ ...old, page: 0 }));
      }
    },
    [serverFilterModel.items],
  );

  const debouncedUpdateServerFilterModel = useMemo(() => {
    return debounce(updateServerFilterModel, 500);
  }, [updateServerFilterModel]);

  useEffect(() => {
    return () => {
      debouncedUpdateServerFilterModel?.cancel();
    };
  }, [debouncedUpdateServerFilterModel]);

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      if (isEqual(filterModel.items, model.items)) {
        return;
      }

      setFilterModel(model);
      debouncedUpdateServerFilterModel?.(model);
      updateServerFilterModel(model);
    },
    [
      debouncedUpdateServerFilterModel,
      filterModel.items,
      updateServerFilterModel,
    ],
  );

  const handlePaginationModelChange = useCallback(
    (
      model: GridPaginationModel,
      details: GridCallbackDetails<'pagination'>,
    ) => {
      if (details.reason === 'setPaginationModel') {
        setPaginationModel((old) => {
          const page = model.pageSize !== old.pageSize ? 0 : model.page;

          return { ...old, page, pageSize: model.pageSize };
        });
      }

      setPaginationModel(model);
    },
    [],
  );

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model?.length ? model : uploadDefaultSort);
    setPaginationModel((old) => ({ ...old, page: 0 }));
  }, []);

  const columns = useMemo(() => {
    if (isBillUpload) {
      return getGridColumns({
        variant: 'bill',
        isSysAdmin,
        setShowPicPreview: props.setShowPicPreview,
        goToStatementDetails: (statementId) => navigate(statementId),
      });
    }

    return getGridColumns({
      variant: 'spreadsheet',
      isSysAdmin,
      tagConfigs: props.tagConfigs,
    });
  }, [isBillUpload, isSysAdmin, navigate, props]);

  const processSpreadsheetRowUpdate = useCallback(
    async (newRow: IImportFile, oldRow: IImportFile) => {
      const updatedTag = `${newRow.tags ?? ''}`;
      const existingTag = `${oldRow.tags ?? ''}`;

      if (updatedTag === existingTag) {
        return oldRow;
      }

      await (
        props as { tagConfigs: UploadHistoryTableTagConfigs }
      ).tagConfigs.onSaveTag({
        rowId: `${newRow.id}`,
        tagValue: updatedTag,
      });

      return newRow;
    },
    [props],
  );

  return (
    <DataTable
      actionsConfig={
        isBillUpload
          ? {
              readActionConfig: {
                onClick: (id) => navigate(id),
              },
            }
          : {
              inRowEditConfig: {
                processRowUpdate: processSpreadsheetRowUpdate,
                btnConfig: () => ({
                  isEditDisabled: props?.tagConfigs?.isSavingTag,
                }),
              },
            }
      }
      checkboxSelection={false}
      columns={columns}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={setColumnVisibilityModel}
      contextName={contextName}
      dataFetchError={isError}
      filterMode="server"
      filterModel={filterModel}
      isRowNavigation={isBillUpload}
      loading={isFetching || (isSpreadsheetUpload ? props.loading : false)}
      onFilterModelChange={handleFilterModelChange}
      onPaginationModelChange={handlePaginationModelChange}
      onSortModelChange={handleSortModelChange}
      paginationMode="server"
      paginationModel={paginationModel}
      rowCount={uploadHistory.meta?.totalCount}
      rows={uploadHistory.data}
      sortingMode="server"
      sortModel={sortModel}
      toolbarSlotProps={{
        showColumns: true,
        showFilters: true,
      }}
    />
  );
};

export default UploadHistoryTable;
